/**
 * A player's feedback on one SimTeach puzzle attempt (issue #4675), written
 * by the webapp at `course/{courseId}/simTeachPuzzleFeedback/{sessionId}`.
 * The doc id is the attempt's session id, so a changed answer overwrites.
 * Firestore rules mirror this shape.
 */
import {z} from 'zod';

import {FirestoreTimestampSchema} from '../firebase/types';
import {hashStringToHex} from '../hash';
import type {SimTeachPuzzle} from './simTeachPuzzle';
import {SimTeachPuzzleOutcomeSchema} from './simTeachPuzzleProgress';

/** The course subcollection holding one feedback doc per rated attempt. */
export const SIMTEACH_PUZZLE_FEEDBACK_COLLECTION = 'simTeachPuzzleFeedback';

/** Whether the attempt was good practice. */
export const SimTeachPuzzleRatingSchema = z.enum(['up', 'down']);

/** Whether the attempt was good practice. */
export type SimTeachPuzzleRating = z.infer<typeof SimTeachPuzzleRatingSchema>;

/** What went wrong, picked after a thumbs-down. */
export const SimTeachPuzzleFeedbackReasonSchema = z.enum([
  'unrealistic-students',
  'inaccurate-grading',
  'broken-functionality',
  'too-hard',
]);

/** What went wrong, picked after a thumbs-down. */
export type SimTeachPuzzleFeedbackReason = z.infer<
  typeof SimTeachPuzzleFeedbackReasonSchema
>;

/** The longest comment the rules accept. */
export const SIMTEACH_PUZZLE_FEEDBACK_COMMENT_MAX = 2000;

/** One attempt's feedback. */
export const SimTeachPuzzleFeedbackSchema = z.object({
  /** The player's uid. */
  uid: z.string(),
  /** The puzzle's id. */
  puzzleId: z.string(),
  /** The attempt's session id: its doc id under `simTeachPuzzleAttempts`. */
  sessionId: z.string(),
  /** How the attempt ended when the player rated it. */
  outcome: SimTeachPuzzleOutcomeSchema,
  /** `simTeachPuzzleVersion` of the puzzle as played. */
  puzzleVersion: z.string(),
  /** Whether it was good practice. */
  rating: SimTeachPuzzleRatingSchema,
  /** What went wrong; empty for a thumbs-up or none picked. */
  reasons: z.array(SimTeachPuzzleFeedbackReasonSchema),
  /** Free text; "" for none. */
  comment: z.string().max(SIMTEACH_PUZZLE_FEEDBACK_COMMENT_MAX),
  /** When it was last written (server time). */
  updatedAt: FirestoreTimestampSchema,
});

/** One attempt's feedback. */
export type SimTeachPuzzleFeedback = z.infer<
  typeof SimTeachPuzzleFeedbackSchema
>;

/**
 * The Firestore path of one attempt's feedback.
 *
 * @param params.courseId - The course the puzzle belongs to.
 * @param params.sessionId - The attempt's session id.
 * @return `course/{courseId}/simTeachPuzzleFeedback/{sessionId}`.
 * @throws Never.
 */
export function simTeachPuzzleFeedbackDocPath({
  courseId,
  sessionId,
}: {
  courseId: string;
  sessionId: string;
}): string {
  return `course/${courseId}/${SIMTEACH_PUZZLE_FEEDBACK_COLLECTION}/${sessionId}`;
}

/** The parts of a puzzle that change how it plays; display flags are left out. */
type PlayedParts = Pick<
  SimTeachPuzzle,
  'title' | 'summary' | 'briefing' | 'goal' | 'cards' | 'students' | 'opening'
>;

/**
 * A short fingerprint of how a puzzle plays, so feedback can be grouped by
 * the version that was played. Publishing, ordering, tags, and slides don't
 * change it.
 *
 * @param puzzle - The puzzle as played.
 * @return An 8-character hex hash (truncated SHA-256).
 * @throws Never.
 */
export function simTeachPuzzleVersion(puzzle: PlayedParts): string {
  const played: PlayedParts = {
    title: puzzle.title,
    summary: puzzle.summary,
    briefing: puzzle.briefing,
    goal: puzzle.goal,
    cards: puzzle.cards,
    students: puzzle.students,
    opening: puzzle.opening,
  };
  // Key-sorted, so Firestore's map order can't change the hash.
  return hashStringToHex(stableStringify(played), 8);
}

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(',')}}`;
  }
  if (value === undefined) return 'null';
  return JSON.stringify(value);
}
